import type { ShopifyProduct } from "@/lib/shopify";

/**
 * Tillbehörsindex — vad det kostar att utrusta en husbil, räknat på våra egna
 * butikspriser. Korgarna matchas mot produktens productType/titel, så priserna
 * följer butiken automatiskt. Ingen post är påhittad: saknas produkten i
 * butiken räknas den inte in, och korgen visar hur många poster den bygger på.
 */

export interface Basket {
  id: string;
  label: string;
  purpose: string;
  /** Regex mot productType + titel. En träff per regel, billigaste vinner. */
  rules: RegExp[];
}

export const BASKETS: Basket[] = [
  {
    id: "vinter",
    label: "Vinterpaketet",
    purpose: "Klara norrbottnisk vinter utan frostskador och kalldrag.",
    rules: [/dieselv[äa]rmare|v[äa]rme/i, /vindrut|isoler/i, /kapell|f[öo]rvaring/i, /fuktm[äa]tare/i],
  },
  {
    id: "el",
    label: "Elpaketet",
    purpose: "Ström för fritidsboende utan el-stolpe.",
    rules: [/litium|batteri/i, /solpanel|solcell/i, /wifi|router|antenn/i],
  },
  {
    id: "vatten",
    label: "Vattenpaketet",
    purpose: "Rent dricksvatten och tank som inte luktar.",
    rules: [/vattenkit|f[äa]rskvatten|slang/i, /tanktillsats/i],
  },
  {
    id: "uppstallning",
    label: "Uppställningspaketet",
    purpose: "Stå plant, kliva in tryggt och rangera själv.",
    rules: [/niv[åa]kil/i, /insteg|trappsteg/i, /mover/i],
  },
  {
    id: "trygghet",
    label: "Trygghetspaketet",
    purpose: "Stöldskydd och fuktkontroll — det som räddar värdet.",
    rules: [/l[åa]s|st[öo]ldskydd/i, /fuktm[äa]tare/i, /tv[äa]ttkit|reng[öo]ring|v[åa]rd/i],
  },
];

export interface BasketRow {
  id: string;
  label: string;
  purpose: string;
  items: { title: string; handle: string; price: number }[];
  total: number;
}

function price(p: ShopifyProduct) {
  return parseFloat(p.node.priceRange.minVariantPrice.amount) || 0;
}

function haystack(p: ShopifyProduct) {
  return `${p.node.productType ?? ""} ${p.node.title}`;
}

export function buildBaskets(products: ShopifyProduct[]): BasketRow[] {
  return BASKETS.map((b) => {
    const used = new Set<string>();
    const items: BasketRow["items"] = [];

    for (const rule of b.rules) {
      const match = products
        .filter((p) => !used.has(p.node.handle) && rule.test(haystack(p)) && price(p) > 0)
        .sort((a, c) => price(a) - price(c))[0];
      if (!match) continue;
      used.add(match.node.handle);
      items.push({ title: match.node.title, handle: match.node.handle, price: price(match) });
    }

    return {
      id: b.id,
      label: b.label,
      purpose: b.purpose,
      items,
      total: items.reduce((sum, i) => sum + i.price, 0),
    };
  }).filter((b) => b.items.length >= 2);
}

export interface CategoryPriceRow {
  type: string;
  n: number;
  lowest: number;
  median: number;
  highest: number;
}

export function buildCategoryPrices(products: ShopifyProduct[]): CategoryPriceRow[] {
  const map = new Map<string, number[]>();
  for (const p of products) {
    const type = (p.node.productType ?? "").trim() || "Övrigt";
    const value = price(p);
    if (value <= 0) continue;
    map.set(type, [...(map.get(type) ?? []), value]);
  }
  return [...map]
    .map(([type, prices]) => {
      const sorted = [...prices].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return {
        type,
        n: sorted.length,
        lowest: sorted[0]!,
        median:
          sorted.length % 2 === 0 ? Math.round((sorted[mid - 1]! + sorted[mid]!) / 2) : sorted[mid]!,
        highest: sorted[sorted.length - 1]!,
      };
    })
    .sort((a, b) => b.n - a.n || a.median - b.median);
}
